import React, { ChangeEvent, Component, Fragment } from 'react';
import { Trans } from 'react-i18next';
import { compose } from 'recompose';
import { FormikProps } from 'formik';
import { Divider, Grid, Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';

import { GroupContentApprovalType } from 'api';
import { ValidatedCheckbox, ValidatedSelect, ValidatedTextField } from 'components';
import {
  withClientLocations,
  withClientDepartments,
  IWithClientLocations,
  IWithClientDepartments,
} from 'providers/queries';
import { IWithFeatures, IWithUser, withFeatures, withUser } from 'providers';
import { isAdminUser } from 'helpers';

import { IGroupForm } from '../../types';

interface IProps {
  formikProps: FormikProps<IGroupForm>;
  type: string;
}

interface IPropsEnhanced
  extends IProps,
    IWithUser,
    IWithFeatures,
    IWithClientLocations,
    IWithClientDepartments,
    WithStyles<typeof styles> {}

const styles = ({ spacing }: Theme) =>
  createStyles({
    divider: {
      marginTop: spacing(5),
      marginBottom: spacing(2),
    },
    dividerLast: {
      marginTop: spacing(2),
      marginBottom: spacing(2),
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(
  withUser,
  withClientLocations,
  withFeatures,
  withClientDepartments,
  withStyles(styles),
);

class FormLeft extends Component<IPropsEnhanced> {
  handleCheckedValue = (value: GroupContentApprovalType) => {
    return value === GroupContentApprovalType.Auto;
  };

  handleApproveChange = (event: ChangeEvent<HTMLInputElement>, value: boolean) => {
    const { formikProps } = this.props;
    const { setFieldValue } = formikProps;
    const approvalType = value ? GroupContentApprovalType.Auto : GroupContentApprovalType.Review;

    setFieldValue('content_approval_type', approvalType);
  };

  renderDepartments() {
    const { clientDepartments } = this.props;
    const { client_departments } = clientDepartments;

    if (!client_departments.length) {
      return null;
    }

    return (
      <Grid item xs>
        <ValidatedSelect
          margin="normal"
          name="department_id"
          i18nKey="user:department"
          items={client_departments}
        />
      </Grid>
    );
  }

  renderLocations() {
    const { clientLocations } = this.props;
    const { client_locations } = clientLocations;

    if (!client_locations.length) {
      return null;
    }

    return (
      <Grid item xs>
        <ValidatedSelect
          margin="normal"
          name="location_id"
          i18nKey="user:location"
          items={client_locations}
        />
      </Grid>
    );
  }

  render() {
    const { classes, features, user } = this.props;

    return (
      <Fragment>
        <ValidatedTextField name="title" i18nKey="groups:name" />
        <Grid container spacing={2}>
          {this.renderDepartments()}
          {this.renderLocations()}
        </Grid>
        <Divider className={classes.divider} />
        <Typography paragraph variant="body2" color="inherit">
          <Trans i18nKey="groups:permissions" />
        </Typography>
        <ValidatedCheckbox
          fullWidth
          name="content_approval_type"
          i18nKey="groups:autoApprove"
          onChange={this.handleApproveChange}
          checkedFn={this.handleCheckedValue}
        />
        <ValidatedCheckbox fullWidth name="allow_user_posts" i18nKey="groups:userPosts" />
        <ValidatedCheckbox fullWidth name="read_only" i18nKey="groups:readOnly" />
        {features.privateGroups && isAdminUser(user) && (
          <ValidatedCheckbox fullWidth name="is_private" i18nKey="groups:private" />
        )}
      </Fragment>
    );
  }
}

export default enhancers(FormLeft);
